import { AppState, Connector, Pin, Wire } from '../types';
import { segmentId } from '../managers/StateManager';

/** One row of the wire cut list: a single wire end-to-end with its measured length. */
export interface CutListRow {
  index: number;
  wireName: string;
  from: EndpointLabel;
  to: EndpointLabel;
  color?: string;
  stripeColor?: string;
  gauge?: number;
  wireType?: string;
  length: number;
  note?: string;
}

/** A resolved wire endpoint: the connector it lands on and, if known, the pin. */
export interface EndpointLabel {
  connector: string;
  pin?: string;
}

/** A connector line item in the bill of materials, aggregated by type. */
export interface BomConnectorRow {
  name: string;
  manufacturer?: string;
  partNumber?: string;
  quantity: number;
}

/** A wire line item in the bill of materials, aggregated by gauge/type/colour. */
export interface BomWireRow {
  gauge?: number;
  wireType?: string;
  color?: string;
  count: number;
  totalLength: number;
}

export interface Bom {
  connectors: BomConnectorRow[];
  wires: BomWireRow[];
  connectorCount: number;
  wireCount: number;
  totalWireLength: number;
}

/** One row of a connector's pinout table. */
export interface PinoutRow {
  pin: string;
  name?: string;
  wire?: string;
  color?: string;
  destination?: EndpointLabel;
}

export interface ConnectorPinout {
  connector: string;
  type?: string;
  rows: PinoutRow[];
}

/** Sums the lengths of the segments a wire's route passes through. */
function wireLength(state: AppState, wire: Wire): number {
  const route = [wire.startConnectorId, ...wire.path, wire.endConnectorId];
  const lengthById = new Map(state.segments.map(s => [s.id, s.length]));
  let total = 0;
  for (let i = 0; i < route.length - 1; i++) {
    total += lengthById.get(segmentId(route[i], route[i + 1])) ?? 0;
  }
  return total;
}

function pinLabel(connector: Connector | undefined, pinId: string | undefined): string | undefined {
  if (!connector || !pinId) {
    return undefined;
  }
  return (connector.pins ?? []).find((pin: Pin) => pin.id === pinId)?.label;
}

function endpoint(connectors: Map<string, Connector>, connectorId: string, pinId?: string): EndpointLabel {
  const connector = connectors.get(connectorId);
  return {
    connector: connector?.name ?? 'unknown',
    pin: pinLabel(connector, pinId)
  };
}

/** Builds the wire cut list: one row per wire, sorted by wire name. */
export function buildCutList(state: AppState): CutListRow[] {
  const connectors = new Map(state.connectors.map(c => [c.id, c]));
  return [...state.wires]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
    .map((wire, index) => ({
      index: index + 1,
      wireName: wire.name,
      from: endpoint(connectors, wire.startConnectorId, wire.startPinId),
      to: endpoint(connectors, wire.endConnectorId, wire.endPinId),
      color: wire.color,
      stripeColor: wire.stripeColor,
      gauge: wire.gauge,
      wireType: wire.wireType,
      length: wireLength(state, wire),
      note: wire.note || undefined
    }));
}

/** Builds the bill of materials: connectors grouped by type, wires grouped by spec. */
export function buildBom(state: AppState): Bom {
  const typeById = new Map(state.connectorTypes.map(t => [t.id, t]));

  const connectorGroups = new Map<string, BomConnectorRow>();
  state.connectors.forEach(connector => {
    const type = connector.connectorTypeId ? typeById.get(connector.connectorTypeId) : undefined;
    const key = type ? `type:${type.id}` : `name:${connector.name}`;
    const existing = connectorGroups.get(key);
    if (existing) {
      existing.quantity += 1;
      return;
    }
    connectorGroups.set(key, {
      name: type?.name ?? connector.name,
      manufacturer: type?.manufacturer,
      partNumber: type?.partNumber,
      quantity: 1
    });
  });

  const wireGroups = new Map<string, BomWireRow>();
  let totalWireLength = 0;
  state.wires.forEach(wire => {
    const length = wireLength(state, wire);
    totalWireLength += length;
    const key = `${wire.gauge ?? ''}|${wire.wireType ?? ''}|${wire.color ?? ''}`;
    const existing = wireGroups.get(key);
    if (existing) {
      existing.count += 1;
      existing.totalLength += length;
      return;
    }
    wireGroups.set(key, {
      gauge: wire.gauge,
      wireType: wire.wireType,
      color: wire.color,
      count: 1,
      totalLength: length
    });
  });

  return {
    connectors: Array.from(connectorGroups.values()).sort((a, b) => a.name.localeCompare(b.name)),
    wires: Array.from(wireGroups.values()).sort((a, b) => (a.gauge ?? 0) - (b.gauge ?? 0)),
    connectorCount: state.connectors.length,
    wireCount: state.wires.length,
    totalWireLength
  };
}

/** Builds a pinout table per connector: which wire lands on each pin and where it goes. */
export function buildPinouts(state: AppState): ConnectorPinout[] {
  const connectors = new Map(state.connectors.map(c => [c.id, c]));
  const typeById = new Map(state.connectorTypes.map(t => [t.id, t]));

  return [...state.connectors]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
    .map(connector => {
      const rows: PinoutRow[] = [];
      (connector.pins ?? []).forEach(pin => {
        const wires = state.wires.filter(
          wire =>
            (wire.startConnectorId === connector.id && wire.startPinId === pin.id) ||
            (wire.endConnectorId === connector.id && wire.endPinId === pin.id)
        );
        if (wires.length === 0) {
          rows.push({ pin: pin.label, name: pin.name });
          return;
        }
        wires.forEach(wire => {
          const atStart = wire.startConnectorId === connector.id && wire.startPinId === pin.id;
          const destination = atStart
            ? endpoint(connectors, wire.endConnectorId, wire.endPinId)
            : endpoint(connectors, wire.startConnectorId, wire.startPinId);
          rows.push({ pin: pin.label, name: pin.name, wire: wire.name, color: wire.color, destination });
        });
      });
      return {
        connector: connector.name,
        type: connector.connectorTypeId ? typeById.get(connector.connectorTypeId)?.name : undefined,
        rows
      };
    });
}
